import React from "react";

import { Card, CardContent, CardTitle } from "@components/ui/card";
import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import { Label } from "@components/ui/label";
import type { PaymentSetupViewProps as _PaymentSetupViewProps } from "@ps-bridge/contracts/paymentSetup";
import * as styles from "@styles/Payment.css";
import type { Ok } from "@ps-bridge/ok";
import type { OptionTs } from "@ps-bridge/interop";
import EmptyView from "@view/EmptyView";
import SquareCardForm, {
  SquareBillingCardCapture,
  type SquareBillingCardInput,
  type SquareBillingContact,
} from "@view/payment/SquareCardForm";
import * as Option from "@utils/Option";

export type BillingAddressForm = {
  firstName: string;
  lastName: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  region: string;
  country: string;
  postalCode: string;
};

export type BillingAddressField = keyof BillingAddressForm;

export type SquareBillingContactInput = {
  givenName: string;
  familyName: string;
  email: string;
  phone: string;
  addressLine1: string;
  addressLine2: OptionTs<string>;
  city: string;
  region: string;
  country: string;
  postalCode: string;
};

export type SquareBillingInput = {
  card: SquareBillingCardInput;
  billingContact: SquareBillingContactInput;
};

export const toSquareBillingContactInput = (
  billingContact: SquareBillingContact,
): SquareBillingContactInput => ({
  givenName: billingContact.givenName,
  familyName: billingContact.familyName,
  email: billingContact.email,
  phone: billingContact.phone,
  addressLine1: billingContact.addressLines[0] ?? "",
  addressLine2: billingContact.addressLines[1]
    ? Option.some(billingContact.addressLines[1])
    : Option.none<string>(),
  city: billingContact.city,
  region: billingContact.region,
  country: billingContact.country,
  postalCode: billingContact.postalCode,
});

export type SquarePaymentSetupViewProps = {
  billingAddress: BillingAddressForm;
  isSubmitting: boolean;
  errorMessage: string;
  onInputChange: (field: BillingAddressField) => (event: React.ChangeEvent<HTMLInputElement>) => Ok;
  onTokenize: (token: string) => Ok;
  onError: (message: string) => Ok;
  billingContact: SquareBillingContact;
};

export type SquareSignupBillingViewProps = {
  billingAddress: BillingAddressForm;
  isSubmitting: boolean;
  errorMessage: string;
  onInputChange: (field: BillingAddressField) => (event: React.ChangeEvent<HTMLInputElement>) => Ok;
  onError: (message: string) => Ok;
  billingContact: SquareBillingContact;
  onSquareBillingChange: (value: OptionTs<SquareBillingInput>) => Ok;
  allowStubCardCapture: boolean;
  tokenizeRequestNonce: number;
  showCardForm: boolean;
};

const SquarePaymentSetupView: React.FC<SquarePaymentSetupViewProps> = ({
  billingAddress,
  isSubmitting,
  errorMessage,
  onInputChange,
  onTokenize,
  onError,
  billingContact,
}) => (
  <div className={styles.container}>
      <Card className={styles.card}>
        <CardContent>
          <CardTitle className={styles.title}>Billing Information</CardTitle>
          <p className={styles.subtitle}>Please enter your billing address and card details.</p>

          <div>
            <div className={styles.row}>
              <div className={styles.field}>
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={billingAddress.firstName}
                  onChange={onInputChange("firstName")}
                  placeholder="First Name"
                  disabled={isSubmitting}
                />
              </div>
              <div className={styles.field}>
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={billingAddress.lastName}
                  onChange={onInputChange("lastName")}
                  placeholder="Last Name"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className={styles.field}>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={billingAddress.phone}
                onChange={onInputChange("phone")}
                placeholder="Phone Number"
                disabled={isSubmitting}
              />
            </div>

            <div className={styles.field}>
              <Label htmlFor="addressLine1">Address Line 1</Label>
              <Input
                id="addressLine1"
                value={billingAddress.addressLine1}
                onChange={onInputChange("addressLine1")}
                placeholder="Street Address"
                disabled={isSubmitting}
              />
            </div>

            <div className={styles.field}>
              <Label htmlFor="addressLine2">Address Line 2</Label>
              <Input
                id="addressLine2"
                value={billingAddress.addressLine2}
                onChange={onInputChange("addressLine2")}
                placeholder="Apt, Suite, etc."
                disabled={isSubmitting}
              />
            </div>

            <div className={styles.row}>
              <div className={styles.field}>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={billingAddress.city}
                  onChange={onInputChange("city")}
                  placeholder="City"
                  disabled={isSubmitting}
                />
              </div>
              <div className={styles.field}>
                <Label htmlFor="region">State/Region</Label>
                <Input
                  id="region"
                  value={billingAddress.region}
                  onChange={onInputChange("region")}
                  placeholder="State"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.field}>
                <Label htmlFor="postalCode">Billing Postal Code</Label>
                <Input
                  id="postalCode"
                  value={billingAddress.postalCode}
                  onChange={onInputChange("postalCode")}
                  placeholder="Billing Postal Code"
                  disabled={isSubmitting}
                />
                <p className={styles.helperText}>
                  Must match the postal code you enter in the card form below.
                </p>
              </div>
              <div className={styles.field}>
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={billingAddress.country}
                  onChange={onInputChange("country")}
                  placeholder="2-letter code, e.g., US"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>

          <div className={styles.errorMessage} aria-live="polite">
            {errorMessage}
          </div>
        </CardContent>
      </Card>

      <SquareCardForm
        onTokenize={onTokenize}
        onError={onError}
        billingContact={billingContact}
        isSubmitting={isSubmitting}
        embedded={false}
        showPostalCode={Boolean(billingAddress.postalCode.trim())}
      />
    </div>
);

export const SquareSignupBillingView: React.FC<SquareSignupBillingViewProps> = ({
  billingAddress,
  isSubmitting,
  errorMessage,
  onInputChange,
  onError,
  billingContact,
  onSquareBillingChange,
  allowStubCardCapture,
  tokenizeRequestNonce,
  showCardForm,
}) => {
  const stubCaptureView = {
    disabled: <EmptyView />,
    enabled: (
      <Card className={styles.embeddedCard}>
        <CardContent>
          <CardTitle className={styles.title}>Stub Billing Capture</CardTitle>
          <p className={styles.subtitle}>
            Test-only fallback for stub-client Playwright coverage when the live secure card form is not bootstrapped.
          </p>
          <Button
            className={styles.button}
            onClick={() =>
              onSquareBillingChange(
                Option.some({
                  card: {
                    sourceId: "stub-square-signup-card",
                    verificationToken: Option.none<string>(),
                  },
                  billingContact: toSquareBillingContactInput(billingContact),
                }),
              )
            }
            disabled={isSubmitting}
            data-testid="create-account-square-billing-stub-save"
            asChild={Option.none()}
          >
            Use stub billing details
          </Button>
        </CardContent>
      </Card>
    ),
  } as const;
  const cardFormView = {
    ready: (
      <SquareBillingCardCapture
        onCardInput={(card) =>
          Option.fold(
            card,
            () => {
              onError("Failed to tokenize card.");
              return onSquareBillingChange(Option.none<SquareBillingInput>());
            },
            (value) =>
              onSquareBillingChange(
                Option.some({
                  card: value,
                  billingContact: toSquareBillingContactInput(billingContact),
                }),
              ),
          )
        }
        billingContact={billingContact}
        isSubmitting={isSubmitting}
        embedded={true}
        postalCode={billingAddress.postalCode.trim()}
        buttonVisibility="hidden"
        tokenizeRequestNonce={tokenizeRequestNonce}
      />
    ),
    pending: (
      <Card className={styles.embeddedCard}>
        <CardContent>
          <CardTitle className={styles.title}>Payment Information</CardTitle>
          <p className={styles.subtitle}>
            Complete the required billing fields above to load the secure card form.
          </p>
        </CardContent>
      </Card>
    ),
  } as const;

  return (
    <div className={styles.embeddedContainer}>
      <Card className={styles.embeddedCard}>
        <CardContent>
          <CardTitle className={styles.title}>Billing Information</CardTitle>
          <p className={styles.subtitle}>Please enter your billing address and card details.</p>

          <div>
            <div className={styles.row}>
              <div className={styles.field}>
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={billingAddress.firstName}
                  onChange={onInputChange("firstName")}
                  placeholder="First Name"
                  disabled={isSubmitting}
                  required
                />
              </div>
              <div className={styles.field}>
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={billingAddress.lastName}
                  onChange={onInputChange("lastName")}
                  placeholder="Last Name"
                  disabled={isSubmitting}
                  required
                />
              </div>
            </div>

            <div className={styles.field}>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={billingAddress.phone}
                onChange={onInputChange("phone")}
                placeholder="Phone Number"
                disabled={isSubmitting}
              />
            </div>

            <div className={styles.field}>
              <Label htmlFor="addressLine1">Address Line 1</Label>
              <Input
                id="addressLine1"
                value={billingAddress.addressLine1}
                onChange={onInputChange("addressLine1")}
                placeholder="Street Address"
                disabled={isSubmitting}
                required
              />
            </div>

            <div className={styles.field}>
              <Label htmlFor="addressLine2">Address Line 2</Label>
              <Input
                id="addressLine2"
                value={billingAddress.addressLine2}
                onChange={onInputChange("addressLine2")}
                placeholder="Apt, Suite, etc."
                disabled={isSubmitting}
              />
            </div>

            <div className={styles.row}>
              <div className={styles.field}>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={billingAddress.city}
                  onChange={onInputChange("city")}
                  placeholder="City"
                  disabled={isSubmitting}
                  required
                />
              </div>
              <div className={styles.field}>
                <Label htmlFor="region">State/Region</Label>
                <Input
                  id="region"
                  value={billingAddress.region}
                  onChange={onInputChange("region")}
                  placeholder="State"
                  disabled={isSubmitting}
                  required
                />
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.field}>
                <Label htmlFor="postalCode">Billing Postal Code</Label>
                <Input
                  id="postalCode"
                  value={billingAddress.postalCode}
                  onChange={onInputChange("postalCode")}
                  placeholder="Billing Postal Code"
                  disabled={isSubmitting}
                  required
                />
                <p className={styles.helperText}>
                  Must match the postal code you enter in the card form below.
                </p>
              </div>
              <div className={styles.field}>
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={billingAddress.country}
                  onChange={onInputChange("country")}
                  placeholder="2-letter code, e.g., US"
                  disabled={isSubmitting}
                  maxLength={2}
                  minLength={2}
                  required
                />
              </div>
            </div>
          </div>

          <div className={styles.errorMessage} aria-live="polite">
            {errorMessage}
          </div>
        </CardContent>
      </Card>

      {cardFormView[showCardForm ? "ready" : "pending"]}
      {stubCaptureView[allowStubCardCapture ? "enabled" : "disabled"]}
    </div>
  );
};

export default SquarePaymentSetupView;
